import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToWishlist, removeFromWishlist, checkWishlistStatus } from '../../services/wishlist';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'react-toastify';
import http from '../../lib/http';
import '../../styles/ProductGrid.css';
import placeholder from '../../assets/images/product.png';

/* ── SVG Icons ── */
const HeartIcon = ({ filled }) => (
  <svg width="15" height="15" viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);

/* ── Star renderer ── */
const renderStars = (rating) => {
  const full = Math.floor(Number(rating || 0));
  const hasHalf = (Number(rating || 0) % 1) >= 0.5;
  const empty = 5 - full - (hasHalf ? 1 : 0);
  const stars = [];

  for (let i = 0; i < full; i++) stars.push(
    <svg key={`f${i}`} className="pg-star filled" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
    </svg>
  );
  if (hasHalf) stars.push(
    <svg key="half" className="pg-star half" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <defs>
        <linearGradient id="pgHalf">
          <stop offset="50%" stopColor="#f77321"/><stop offset="50%" stopColor="#dde3ed"/>
        </linearGradient>
      </defs>
      <path fill="url(#pgHalf)" d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
    </svg>
  );
  for (let i = 0; i < empty; i++) stars.push(
    <svg key={`e${i}`} className="pg-star empty" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
    </svg>
  );
  return stars;
};

/* ════════════════════════════════════════ */
const ProductGrid = ({ products, viewMode = "grid" }) => {
  const navigate = useNavigate();
  const { updateCartCount } = useCart();
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [cartLoadingIds, setCartLoadingIds] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/api/customer/wishlists');
        const items = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const ids = items.map(i => i.product_id || i.product?.id).filter(Boolean);
        setWishlistItems(new Set(ids));
      } catch (e) { console.error('Wishlist load error:', e); }
    })();
  }, []);

  const handleWishlistToggle = async (productId, e) => {
    e.stopPropagation();
    try {
      if (wishlistItems.has(productId)) {
        const item = await checkWishlistStatus(productId);
        if (item) {
          await removeFromWishlist(item.id);
          setWishlistItems(prev => { const s = new Set(prev); s.delete(productId); return s; });
          toast.success('Removed from wishlist');
        }
      } else {
        await addToWishlist(productId);
        setWishlistItems(prev => new Set([...prev, productId]));
        toast.success('Added to wishlist');
      }
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to update wishlist.');
    }
  };

  const handleAddToCart = async (productId, e) => {
    e.stopPropagation();
    setCartLoadingIds(prev => new Set([...prev, productId]));
    try {
      await http.post('/api/customer/carts', { product_id: productId, quantity: 1 });
      toast.success('Added to cart!');
      updateCartCount();
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setCartLoadingIds(prev => { const s = new Set(prev); s.delete(productId); return s; });
    }
  };

  if (!products || products.length === 0) return null;

  return (
    <div className={`pg-grid ${viewMode === 'list' ? 'pg-list' : ''}`}>
      {products.map((product, idx) => {
        const inWishlist  = wishlistItems.has(product.id);
        const cartLoading = cartLoadingIds.has(product.id);
        return (
          <article
            key={product.id}
            className="pg-card"
            style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
            onClick={() => navigate(`/product/${product.id}`)}
            role="button" tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/product/${product.id}`)}
          >
            {/* ── Image block ── */}
            <div className="pg-img-wrap">
              <img
                className="pg-img"
                src={product.image || placeholder}
                alt={product.name}
                loading="lazy"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholder; }}
              />

              {/* Wishlist heart — top right */}
              <button
                className={`pg-heart ${inWishlist ? 'is-saved' : ''}`}
                onClick={(e) => handleWishlistToggle(product.id, e)}
                aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <HeartIcon filled={inWishlist}/>
              </button>

              {/* Price badge — top left */}
              {product.isFlashSale && (
                <span className="pg-badge pg-badge-flash">⚡ Flash</span>
              )}
              {!product.isFlashSale && product.originalPrice > 0 && product.price < product.originalPrice && (
                <span className="pg-badge pg-badge-discount">
                  -{Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                </span>
              )}

              {/* Hover overlay — "Add to Cart" slides up */}
              <div className="pg-overlay">
                <button
                  className="pg-cart-btn"
                  onClick={(e) => handleAddToCart(product.id, e)}
                  disabled={cartLoading}
                >
                  {cartLoading
                    ? <span className="pg-cart-spin"/>
                    : <><CartIcon/> Add to Cart</>
                  }
                </button>
              </div>
            </div>

            {/* ── Body ── */}
            <div className="pg-body">
              {/* Name */}
              <h3 className="pg-name">{product.name}</h3>

              {/* Stars row */}
              <div className="pg-rating-row">
                <span className="pg-stars-wrap">{renderStars(product.rating)}</span>
                {Number(product.reviews) > 0 && (
                  <span className="pg-review-count">({product.reviews})</span>
                )}
                {product.sold && <span className="pg-sold-badge">{product.sold}</span>}
              </div>

              {/* Price + arrow ── separated cleanly */}
              <div className="pg-footer">
                <div className="pg-price-block">
                  <span className={`pg-price${product.isFlashSale ? ' pg-price-flash' : ''}`}>
                    ${Number(product.price || 0).toFixed(2)}
                  </span>
                  {product.originalPrice > 0 && product.price < product.originalPrice && (
                    <span className="pg-price-original">${Number(product.originalPrice).toFixed(2)}</span>
                  )}
                </div>
                <button
                  className="pg-arrow-btn"
                  onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                  aria-label={`View ${product.name}`}
                >
                  <ArrowIcon/>
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default ProductGrid;